import { TAG_COLOR, TAG_SORT, SEPERATOR } from "../../constants/tagConstants";
import Tag from "../../models/Tag";

export function extractCategories(graphData) {
  const catSet = graphData.reduce((a, i) => a.add(i.tagType), new Set());
  const sortScores = TAG_SORT;
  return Array.from(catSet.values())
    .map((x) => ({ name: x, sortScore: sortScores[x] }))
    .sort((a, b) => a.sortScore - b.sortScore)
    .filter((x) => x.sortScore)
    .map((x) => x.name);
}

export function parseTag(x) {
  const { key, doc_count: count } = x;
  const parts = key.split(SEPERATOR);
  const year = Number(parts[0]);
  const tagType = parts[1];
  const normalized = parts.slice(2).join(SEPERATOR);

  if (!TAG_SORT[tagType]) return null;

  return new Tag({ tagType, year, normalized, count });
}

function calcYearFilter(tags) {
  const limit = 1000;
  const yearOrdered = tags.sort((a, b) => b.year - a.year);
  const yearLimit = new Date().getFullYear() - 10;

  let yearCount = 0;
  let minYear = Infinity;

  for (const tag of yearOrdered) {
    yearCount += 1;
    minYear = tag.year > yearLimit ? yearLimit : tag.year;

    if (yearCount >= limit) {
      break;
    }
  }

  return tags.filter((x) => x?.year && x.year >= minYear);
}

export function parseTags(tags) {
  const tagObjs = tags.map(parseTag).filter(Boolean);
  const yearFiltered = tagObjs.filter(Boolean);
  return calcYearFilter(yearFiltered);
}

export function filterData(data, selectedCategory) {
  const items = [];
  const limitPerCategory = selectedCategory ? 1000 : 30;
  const maxMentionLimit = 300;
  const typeCountDict = new Map();
  const idDict = new Map();
  const idCountDict = new Map();
  const catToIdDict = new Map();
  const idToMaxYear = new Map();
  const noveltyFactorMap = new Map();

  const unboundTags = selectedCategory
    ? data.filter((x) => x.tag.tagType === selectedCategory)
    : data;

  for (let i = 0; i < unboundTags.length; i++) {
    const x = unboundTags[i];
    const { tagType, totalCount, tag, count } = unboundTags[i];
    const { typeKey: id } = tag;

    typeCountDict.set(tagType, (typeCountDict.get(tagType) || 0) + count);
    idDict.set(id, (idDict.get(id) || []).concat([x]));
    idCountDict.set(id, totalCount);
    catToIdDict.set(tagType, (catToIdDict.get(tagType) || new Set()).add(id));
    idToMaxYear.set(id, Math.max(idToMaxYear.get(id) || 0, x.year));
    noveltyFactorMap.set(id, x.noveltyFactor);
  }

  for (const category of typeCountDict.keys()) {
    const filtered = Array.from(catToIdDict.get(category).values())
      .filter((x) => idCountDict.get(x) < maxMentionLimit)
      .sort((a, b) => noveltyFactorMap.get(b) - noveltyFactorMap.get(a))
      .slice(0, limitPerCategory)
      .map((x) => idDict.get(x))
      .reduce((a, i) => a.concat(i), []);

    items.push(...filtered);
  }

  const uniqueTagMap = items.reduce(
    (a, i) => a.set(i.tag.typeKey, i),
    new Map()
  );

  const unboundTagMap = items.reduce(
    (a, i) => a.set(i.tag.typeKey, i),
    new Map()
  );

  return {
    unboundTags,
    tags: items,
    uniqueTagMap,
    unboundTagMap,
    uniqueTags: Array.from(uniqueTagMap.values()),
    unboundUniqueTags: Array.from(unboundTagMap.values()),
  };
}

function findSlope(valuesX, valuesY) {
  const { length: len } = valuesX;
  let x = 0;
  let y = 0;
  let xSum = 0;
  let ySum = 0;
  let xySum = 0;
  let xxSum = 0;

  for (let i = 0; i < len; i++) {
    x = valuesX[i];
    y = valuesY[i];
    xSum += x;
    ySum += y;
    xxSum += x * x;
    xySum += x * y;
  }

  return (len * xySum - xSum * ySum) / (len * xxSum - xSum * xSum);
}

export function expandTagsPerYear(tagArr) {
  if (!tagArr.length) return [];

  const yearTotalCountMap = new Map();
  const tagTotalCountMap = new Map();
  const tagInYearsMap = new Map();
  const tagCountMap = new Map();
  const lineMap = new Map();

  for (const tag of tagArr) {
    const { year: y, count: c, typeKey: k, key: yk } = tag;
    const yearCount = yearTotalCountMap.get(y) || 0;
    const idCount = tagTotalCountMap.get(k) || 0;
    const tagYears = tagInYearsMap.get(k) || new Set();
    const tagCount = tagCountMap.get(k) || 0;

    yearTotalCountMap.set(y, yearCount + c);
    tagTotalCountMap.set(k, idCount + c);
    tagCountMap.set(yk, tagCount + c);
    tagInYearsMap.set(k, tagYears.add(y));
  }

  const minYear = Math.min(...Array.from(yearTotalCountMap.keys()));
  const maxYear = Math.max(...Array.from(yearTotalCountMap.keys()));
  const years = Array(maxYear - minYear + 1)
    .fill(null)
    .map((_, i) => minYear + i);

  const tags = tagArr.map((tag) => {
    const { typeKey: k } = tag;

    if (tagInYearsMap.get(k)?.size > 1 && !lineMap.has(k)) {
      const minY = years.findIndex((y) => tagCountMap.get(`${y}:${k}`));
      const yearArr = years.slice(Math.max(0, minY - 1));
      const valArr = yearArr.map((y) => {
        const yearTotal = yearTotalCountMap.get(y);
        const tagYear = tagCountMap.get(`${y}:${k}`) || 0;
        return tagYear ? tagYear / yearTotal : -50;
      });

      lineMap.set(tag.typeKey, findSlope(yearArr, valArr));
    }

    return {
      count: tag.count,
      pointId: tag.key,
      normalized: tag.normalized,
      year: tag.year,
      tagType: tag.tagType,
      fillClass: `${TAG_COLOR[tag.tagType]} tw-fill-current`,
      totalCount: tagTotalCountMap.get(tag.typeKey),
      noveltyFactor: lineMap.get(tag.typeKey) || -50,
      tag,
    };
  });

  return tags;
}
